/**
 * M2 bridge, stations (M3 plan §7 item 2, R22): M2 station definitions for every M3 `StationSite` of a system.
 *
 * - `m2Role` picks the M2 template station (`hub`, `refinery` or `scrapyard` in M2's `stations.json`): its services,
 *   outfitting, price multipliers and saturation capacity. Services are the template's, kept only where the site
 *   offers them; the lease service stays with the home cluster's Lease Office.
 * - The market specialty picks what the station sells (`ice`, `ores` or one ware id); components sell nothing.
 * - `priceSeed` becomes the station's comms seed; price drift keys on the (seeded, unique) station id.
 * - The home cluster's three stations keep M2's ids, names, factions, looks and models, at M2's positions offset
 *   by `home.center` (D-TBD 7).
 * - The pose is M2's: `faceToward` a point 1 km out along the site's bay axis (local −Z), and the roll that
 *   reproduces the site's rotation through `stationRotation` (tested).
 *
 * Pure: no allocation concerns (runs once per system switch).
 */
import type { StationSite, SystemLayout } from '../../galaxy/layout/types'
import type { StationDef, StationId, StationsData, WareId } from '../../data/m2Schemas'
import { WARE_IDS } from '../../data/m2Schemas'

/** Distance of the `faceToward` point along the bay axis (metres); any positive value gives the same rotation. */
const FACE_DISTANCE_M = 1000
/** Below this the approach axis counts as vertical (matches `stationRotation`). */
const VERTICAL = 0.999999

/** Wares each specialty sells (M2's home stations: the hub sells ice, the refinery the three common ores). */
const SPECIALTY_SELLS: Readonly<Record<string, readonly WareId[]>> = {
  ice: ['ice'],
  ores: ['ferrite', 'silicates', 'titanium'],
  components: [],
}

/** M3 service names → M2 service names (the rest are M3 tabs: charts, parts). */
const SERVICE_MAP: Readonly<Record<string, StationDef['services'][number]>> = {
  market: 'market',
  repair: 'repair',
  outfitting: 'outfitting',
  contracts: 'contracts',
  'lease-office': 'lease',
}

export interface BridgedStations {
  /** M2 station data for one system (`systemId` = the M3 system id). */
  readonly data: StationsData
  /** M3 site id → M2 station id. */
  readonly bySiteId: ReadonlyMap<string, StationId>
}

/** The M2 station id of an M3 site: a slug (`sys-33-st0` → `sys_33_st0`). */
export function m2StationId(siteId: string): StationId {
  return siteId.toLowerCase().replace(/[^a-z0-9_]/g, '_')
}

/** The M2 ids the home cluster's sites keep (M2's `stations.json` ids, = their site types; `homeStation`). */
export const HOME_M2_IDS: readonly StationId[] = ['hub', 'refinery', 'scrapyard']

/**
 * R3-1: the M2 station id site `siteId` plays in `layout` (`bridgeStations`' rule): in the home system a site whose
 * type is one of M2's stations keeps that id (`sys-07-st0` → `hub`), every other site its slug (`m2StationId`).
 * A site the layout lacks (or no layout) is its slug. Every strategic site id → board id conversion goes through here.
 */
export function m2IdOfSite(siteId: string, layout: Pick<SystemLayout, 'stations' | 'home'> | null, homeIds: readonly string[] = HOME_M2_IDS): StationId {
  if (layout !== null && layout.home !== null) {
    const site = layout.stations.find((s) => s.id === siteId)
    if (site !== undefined && homeIds.includes(site.type)) return site.type
  }
  return m2StationId(siteId)
}

/** An M2 slug of a `<systemId>-st<n>` site id (`sys_07_st2`). */
const SITE_SLUG = /^sys_(\d{2,})_st(0|[1-9]\d*)$/

/**
 * CH-12g: the site id an M2 station slug plays, without a layout (`sys_01_st7` → `sys-01-st7`), or null when the id is
 * not a slug of the `<systemId>-st<n>` form. Every station site has that form: M3's generated sites and the home
 * cluster's (`galaxy/layout/stations.ts`, `homeCluster.ts`) and every station the strategic core creates
 * (`strategic/ids.ts stationId`: AI-built, corp-built, guardian bases), so the slug inverts exactly. The home
 * cluster's three M2 ids (`hub`, `refinery`, `scrapyard`) are their site types, not slugs: those resolve through the
 * home layout. The system may still not exist; the caller checks.
 */
export function siteIdOfM2Slug(stationId: string): string | null {
  const m = SITE_SLUG.exec(stationId)
  if (m === null) return null
  const site = `sys-${m[1]}-st${m[2]}`
  return m2StationId(site) === stationId ? site : null
}

/** What a specialty sells, limited to wares the station prices (never tetherstone). */
export function specialtySells(specialty: string | null, prices: StationDef['prices']): WareId[] {
  if (specialty === null) return []
  const listed = SPECIALTY_SELLS[specialty] ?? ((WARE_IDS as readonly string[]).includes(specialty) ? [specialty as WareId] : [])
  return listed.filter((w) => w !== 'tetherstone' && prices.wares[w] !== undefined)
}

/** M2's pose for a site's rotation: the point the bay faces and the roll about the approach axis (radians). */
export function sitePose(site: Pick<StationSite, 'position' | 'rotation'>): { faceToward: [number, number, number]; roll: number } {
  const [qx, qy, qz, qw] = site.rotation
  // Out = q · (0, 0, −1); up = q · (0, 1, 0) (rotation-matrix columns).
  const ox = -(2 * (qx * qz + qw * qy))
  const oy = -(2 * (qy * qz - qw * qx))
  const oz = -(1 - 2 * (qx * qx + qy * qy))
  const ux = 2 * (qx * qy - qw * qz)
  const uy = 1 - 2 * (qx * qx + qz * qz)
  const uz = 2 * (qy * qz + qw * qx)
  // The level up `stationRotation` uses: world +Y (world +Z when the axis is vertical) made perpendicular to out.
  const vertical = Math.abs(oy) > VERTICAL
  const ry = vertical ? 0 : 1
  const rz = vertical ? 1 : 0
  const d = ry * oy + rz * oz
  let lx = -d * ox
  let ly = ry - d * oy
  let lz = rz - d * oz
  const ll = Math.hypot(lx, ly, lz)
  lx /= ll
  ly /= ll
  lz /= ll
  // Right = out × level up; positive roll turns up toward right.
  const rx = oy * lz - oz * ly
  const rry = oz * lx - ox * lz
  const rrz = ox * ly - oy * lx
  const roll = Math.atan2(ux * rx + uy * rry + uz * rrz, ux * lx + uy * ly + uz * lz)
  const [px, py, pz] = site.position
  return { faceToward: [px + ox * FACE_DISTANCE_M, py + oy * FACE_DISTANCE_M, pz + oz * FACE_DISTANCE_M], roll }
}

function templateFor(home: StationsData, role: string): StationDef {
  const t = home.stations.find((s) => s.id === role)
  if (t === undefined) throw new Error(`bridge: M2 stations.json has no "${role}" template station`)
  return t
}

function homeStation(site: StationSite, layout: Pick<SystemLayout, 'home'>, home: StationsData): StationDef | null {
  if (layout.home === null) return null
  const m2 = home.stations.find((s) => s.id === site.type)
  if (m2 === undefined) return null
  const [cx, cy, cz] = layout.home.center
  return {
    ...structuredClone(m2),
    position: [m2.position[0] + cx, m2.position[1] + cy, m2.position[2] + cz],
    faceToward: [m2.faceToward[0] + cx, m2.faceToward[1] + cy, m2.faceToward[2] + cz],
  }
}

function reachStation(site: StationSite, home: StationsData): StationDef {
  const t = templateFor(home, site.m2Role)
  const offered = new Set<string>()
  for (const s of site.services) {
    const m2 = SERVICE_MAP[s]
    if (m2 !== undefined) offered.add(m2)
  }
  const services = t.services.filter((s) => s !== 'lease' && offered.has(s))
  if (services.length === 0) services.push('repair')
  const market = services.includes('market')
  const prices = structuredClone(t.prices)
  if (site.faction !== 'tethys') delete prices.wares.tetherstone
  const pose = sitePose(site)
  return {
    id: m2StationId(site.id),
    name: site.name,
    faction: site.faction,
    look: site.look,
    assetId: site.assetId,
    baySocket: 'DOCK_BAY_0',
    position: [site.position[0], site.position[1], site.position[2]],
    faceToward: pose.faceToward,
    roll: pose.roll,
    services,
    outfitting: services.includes('outfitting') ? [...t.outfitting] : [],
    prices,
    sells: market ? specialtySells(site.specialty, prices) : [],
    capacityT: t.capacityT,
    commsSeed: site.priceSeed >>> 0,
    index: 0,
  }
}

/** The M2 stations of one system, in site order (`index` = position in the list). */
export function bridgeStations(layout: Pick<SystemLayout, 'systemId' | 'stations' | 'home'>, home: StationsData): BridgedStations {
  const bySiteId = new Map<string, StationId>()
  const stations: StationDef[] = []
  for (const site of layout.stations) {
    const def = homeStation(site, layout, home) ?? reachStation(site, home)
    def.index = stations.length
    stations.push(def)
    bySiteId.set(site.id, def.id)
  }
  return { data: { systemId: layout.systemId, stations }, bySiteId }
}
